import React, { useCallback, useRef } from 'react';
import { ROOMS } from '../rooms';
import { SLICE_DISTRICT } from '../config';
import { useWorldStore } from '../state/worldStore';
import { Performer } from './VenueStage';
import { useGreeting } from '../state/greeting';
import DeskClipboard from './DeskClipboard';
import { useSceneChat } from './SceneChat';

// FrontDesk — whoever stands at a room's front desk (2026-10-31).
//
// Khari: "HypeTunes Main Headquarters (a helpdesk). Place Lyra at the front
// desk." The desk's mark is the room's (rooms.js `frontDesk`, from
// scripts/make_hq.mjs); WHO stands there is `who`: 'assistant' is the
// platform's assistant, whose avatar id the API sends with the world's config
// (one id, kept where the API keeps it — no number copied into the World).
//
// A tap opens the SAME conversation as everywhere else she is (the avatar chat,
// the website thread): the one surface that already knows she is the assistant
// and answers with the platform's knowledge. Nothing new to learn, no second
// brain at the desk — she is simply standing somewhere now.

export default function FrontDesk({ roomId, k = 1 }) {
  const desk = ROOMS[roomId]?.frontDesk || null;
  const avatarId = desk?.who === 'assistant' ? SLICE_DISTRICT.assistantAvatarId || null : Number(desk?.avatarId) || null;
  const npcList = useWorldStore((s) => s.npcList);
  const body = (npcList || []).find((n) => Number(n?.avatarId) === Number(avatarId));
  const name = body?.name || desk?.name || 'the front desk';
  const openChat = useWorldStore((s) => s.openChat);
  const goal = useRef(null);   // she stays at the desk
  // The desk's own preset questions go with the chat (rooms.js `frontDesk.questions`),
  // and it opens as a SCENE (components/SceneChat, `frontDesk.scene`): her face —
  // the mark at her eye line, as RoomGreeting frames it — so the camera comes to
  // her, up close, and the chat lays itself over the shot.
  const onTap = useCallback(() => {
    if (!avatarId) return;
    const eye = (body?.height || 0.75) * 0.905;
    const scene = desk?.scene ? {
      roomId, avatarId,
      face: [desk.pos[0] * k, (desk.pos[1] || 0) * k + eye, desk.pos[2] * k],
      rotY: desk.rotY || 0,
      sub: ROOMS[roomId]?.name || null,
    } : null;
    openChat({ avatarId, name, questions: Array.isArray(desk?.questions) ? desk.questions : null, scene });
  }, [avatarId, name, openChat, desk, roomId, k, body?.height]);
  // HER RECORDED GREETING (components/RoomGreeting): the speaking face while it
  // plays, and word that she is here, so the shot never pushes in on an empty desk.
  const greeting = useGreeting((s) => s.talking === roomId);
  // …and while she speaks to you in her chat (her voice, or a typed reply read out: SceneChat useChatSpeaking).
  const speaking = useSceneChat((s) => s.speaking != null && s.speaking === Number(avatarId));
  const talking = greeting || speaking;
  const onReady = useCallback(() => useGreeting.getState().markDeskReady(roomId), [roomId]);

  if (!desk || !avatarId) return null;
  const light = (ROOMS[roomId]?.light ?? 1) * 0.45;
  return (
    <>
      {/* She emotes as the rooms' hosts do (Performer, city/bodyLanguage; Khari, 2026-11-04: "this fix should
          apply to lyra as well. Talking to her in the HQ, she does not emote"): her hands while she talks to you
          (typed or by voice) and while her greeting plays (`doing: 'words'`), and the quiet habits at the desk. */}
      <Performer a={avatarId} name={name} mark={desk} k={k} light={light} talking={talking} goal={goal} onTap={onTap} onReady={onReady} doing={greeting ? 'words' : null} habits />
      {/* The sign that says she can be talked to (rooms.js `frontDesk.clipboard`). */}
      {desk.clipboard && <DeskClipboard spec={desk.clipboard} k={k} name={name} onTap={onTap} />}
    </>
  );
}
